import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Abrufausfall, useAbruf } from './Abruf'
import { api, imHintergrund } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { gesellschaftsname, periodenname, t } from './sprache'
import { Blattabschnitt, wertwort } from './Stammdaten'
import type { Listeneintrag } from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { Eintrittskurswahl, eintrittskurstext } from './Eintrittskurswahl'
import type { Eintrittskursangabe, Eintrittskursweg } from './Eintrittskurswahl'

/** Der Eintrittskurs, wie er an der Mitgliedschaft steht (R1443). */
function angabeAus(m: Listeneintrag): Eintrittskursangabe {
  return {
    weg: (m.werte.eintrittskurs_weg || null) as Eintrittskursweg | null,
    kurs: m.werte.eintrittskurs ? Number(m.werte.eintrittskurs) : null,
    quelle: m.werte.eintrittskurs_quelle ? Number(m.werte.eintrittskurs_quelle) : null,
  }
}

/** Solange die Mitglieder nicht da sind, steht keine Zeile da — auch keine Leerzeile (R1210). */
const KEINE_MITGLIEDER: Listeneintrag[] = []

/**
 * Wer seit wann zum Konzern gehört — im Blatt des Kreises
 * (2.3, Auftrag vom 2026-09-26).
 *
 * **Die Frage steht in der kausalen Kette des Auftraggebers an erster
 * Stelle:** *„Wie heißt der Konsolidierungskreis, also der Konzern, wer gehört
 * seit wann in welcher Form dazu?"* Bis hierher war die Kreiszugehörigkeit
 * eine eigene Objektart in der Liste links — wer wissen wollte, wer zum
 * Konzern gehört, wechselte den Ort und filterte dort nach dem Kreis.
 *
 * **Sie steht jetzt im Blatt des Kreises**, wie die Zuordnung im Ordnerbaum
 * und die Datei an der Datenquelle (R317): Was nur im Zusammenhang eines
 * Stammdatums Sinn ergibt, wird dort gepflegt.
 *
 * **Die Einbeziehungsart steht daneben, aber nicht hier.** Sie hängt an der
 * **Beteiligung** und nicht an der Mitgliedschaft (2.2): Dieselbe Gesellschaft
 * kann in zwei Kreisen verschieden einbezogen sein, und maßgeblich ist, was
 * die Mutter an ihr hält. Der Abschnitt zeigt sie deshalb nur an — geändert
 * wird sie im Beteiligungsregister.
 */
export function Kreismitglieder({
  kreis,
  aufAenderung,
}: {
  kreis: string
  aufAenderung: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('kreismitglieder:kreismitglieder')

  const [gesellschaften, setGesellschaften] = useState<Listeneintrag[]>([])
  const [beteiligungen, setBeteiligungen] = useState<Listeneintrag[]>([])
  const [stand, setStand] = useState(0)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  /**
   * Ob das Aufnahmeformular offensteht (R315).
   *
   * **Es stand zuerst immer da** — zwei Felder und ein Knopf unter einer
   * Liste, die man meistens nur liest. Der Auftraggeber am 2026-09-26:
   * *„Dieser Teil soll nur aufploppen, wenn ich auf eine Schaltfläche
   * ‚Gesellschaft hinzufügen' klicke."* Dasselbe Muster wie beim
   * Konzernhandbuch und bei den Konsolidierungsschritten, wo es schon so
   * gebaut war.
   */
  const [anlegen, setAnlegen] = useState(false)
  /** Die Mitgliedschaft, deren Eintrittskurs gerade gewählt wird (R1403). */
  const [kurswahl, setKurswahl] = useState<string | null>(null)
  /** Was die Wahl des Eintrittskurses meldet — beim Aufnehmen wie beim Ändern. */
  const [wahl, setWahl] = useState<{ fremd: boolean; felder: Record<string, string> | null }>({ fremd: false, felder: {} })
  const [kreise, setKreise] = useState<Listeneintrag[]>([])
  const [entwurf, setEntwurf] = useState<{ einheit: string; von: number | null }>({
    einheit: '',
    von: null,
  })
  const darf = useDarf()

  // Die Mitglieder sind der Inhalt des Abschnitts — ein Hauptabruf (R1211).
  const mitgliederabruf = useAbruf(
    () =>
      api
        .stammdatenliste('kreis_einheit')
        .then((l) => l.eintraege.filter((e) => e.werte.kreis === kreis)),
    [kreis, stand],
  )
  const mitglieder = mitgliederabruf.wert ?? KEINE_MITGLIEDER

  // Namen und Einbeziehungsart: Nachschlagen im Hintergrund (7.2 Abschnitt 30, R1007).
  useEffect(() => {
    let gilt = true
    imHintergrund(() => api.stammdatenliste('einheit'))
      .then((l) => gilt && setGesellschaften(l.eintraege))
      .catch(() => {})
    imHintergrund(() => api.stammdatenliste('beteiligung'))
      .then((l) => gilt && setBeteiligungen(l.eintraege))
      .catch(() => {})
    imHintergrund(() => api.stammdatenliste('konsolidierungskreis'))
      .then((l) => gilt && setKreise(l.eintraege))
      .catch(() => {})

    return () => {
      gilt = false
    }
  }, [])

  const nachSchluessel = useMemo(
    () => new Map(gesellschaften.map((g) => [g.schluessel, g])),
    [gesellschaften],
  )

  /**
   * Wie eine Gesellschaft in diesem Kreis einbezogen ist.
   *
   * **Über die Beteiligung, an der sie Tochter ist** — dort steht die
   * Entscheidung (2.2). Findet sich keine, ist sie die oberste Gesellschaft
   * des Kreises: Sie gehört dazu, weil der Konzern von ihr aus gerechnet wird,
   * und nicht, weil jemand sie hält.
   */
  function art(einheit: string): string {
    const b = beteiligungen.find((x) => x.werte.tochter === einheit)
    // **Dieselbe Beschriftung wie im Blatt daneben** — zwei Wörter für
    // dieselbe Einbeziehungsart gehen mit der Zeit auseinander (R778).
    return b?.werte.einbeziehungsart
      ? wertwort('einbeziehungsart', b.werte.einbeziehungsart)
      : t('kreis.mitglied.oberste')
  }

  /**
   * Ob eine Gesellschaft in fremder Währung bilanziert — gemessen an der
   * Konzernwährung dieses Kreises, der Währung seiner obersten Gesellschaft
   * (R1443). `null`, solange die Währungen nicht da sind.
   */
  function fremd(einheit: string): boolean | null {
    const spitze = kreise.find((k) => k.schluessel === kreis)?.werte.oberste_einheit
    const konzernwaehrung = spitze ? nachSchluessel.get(spitze)?.werte.waehrung : undefined
    const waehrung = nachSchluessel.get(einheit)?.werte.waehrung
    return konzernwaehrung && waehrung ? waehrung !== konzernwaehrung : null
  }

  const zeilen = useMemo(
    () =>
      [...mitglieder].sort((a, b) =>
        (a.werte.gueltig_von ?? '').localeCompare(b.werte.gueltig_von ?? ''),
      ),
    [mitglieder],
  )

  const melde = useCallback((istFremd: boolean, felder: Record<string, string> | null) => setWahl({ fremd: istFremd, felder }), [])

  async function tue(was: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)

    try {
      await was()
      setStand((n) => n + 1)
      setEntwurf({ einheit: '', von: null })
      setAnlegen(false)
      setKurswahl(null)
      aufAenderung()
    } catch (fehler) {
      // Nur die fachliche Absage steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  // **Wer schon drin ist, steht nicht zur Wahl** (R327): Eine Gesellschaft
  // zweimal im selben Kreis wäre keine zweite Mitgliedschaft, sondern ein
  // Fehler -- und zwar einer, der die Summierung verdoppelt.
  const waehlbar = useMemo(
    () =>
      gesellschaften
        .filter((g) => !mitglieder.some((m) => m.werte.einheit === g.schluessel))
        .map(ausStammdatum),
    [gesellschaften, mitglieder],
  )

  return (
    <Blattabschnitt
      kennung="kreis.mitglieder"
      titel={t('kreis.mitglieder.titel')}
      // Ohne „12 Gesellschaften" (7.2 Abschnitt 36, R1143).
      zusatz={undefined}
    >
      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="kreismitglieder ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('kreis.mitglied.gesellschaft')}</th>
              <th>{t('kreis.mitglied.ab')}</th>
              <th>{t('kreis.mitglied.bis')}</th>
              <th>{t('kreis.mitglied.art')}</th>
              <th>{t('kreis.mitglied.eintrittskurs')}</th>
              {darf('aussondern') && <th />}
            </tr>
          </thead>
          <tbody>
            {/* „Noch keine Gesellschaft aufgenommen" nur, wenn die Liste kam und leer war (R1210). */}
            {mitgliederabruf.ausgefallen && (
              <tr>
                <td colSpan={6}>
                  <Abrufausfall abruf={mitgliederabruf} />
                </td>
              </tr>
            )}
            {mitgliederabruf.wert !== null && zeilen.length === 0 && (
              <tr>
                <td colSpan={6} className="leerhinweis">
                  {t('kreis.mitglieder.leer')}
                </td>
              </tr>
            )}
            {zeilen.map((m) => {
              const einheit = m.werte.einheit ?? ''
              const angabe = angabeAus(m)
              const istFremd = fremd(einheit)
              // Über den Namensgeber, nicht aus der eigenen Liste (R1660).
              const name = gesellschaftsname(einheit)
              return (
                <Fragment key={m.schluessel}>
                <tr>
                  <td>{name}</td>
                  <td>{m.werte.gueltig_von ? periodenname(Number(m.werte.gueltig_von)) : ''}</td>
                  <td>
                    {m.werte.gueltig_bis
                      ? periodenname(Number(m.werte.gueltig_bis))
                      : t('kreis.mitglied.offen')}
                  </td>
                  <td className="gedaempft">{art(einheit)}</td>
                  {/* **Der Eintrittskurs, sobald die Gesellschaft in fremder Währung bilanziert** (R1403, R1443). */}
                  <td>
                    {istFremd || angabe.weg ? eintrittskurstext(angabe) : ''}
                    {istFremd && darf('stammdatumPflegen') && kurswahl !== m.schluessel && (
                      <button type="button" className="schmal" disabled={laeuft} onClick={() => setKurswahl(m.schluessel)}>
                        {t(angabe.weg ? 'kreis.eintrittskurs.aendern' : 'kreis.eintrittskurs.waehlen')}
                      </button>
                    )}
                  </td>
                  {/* Ohne Recht zum Aussondern fehlt die Spalte samt Knopf (R1180). */}
                  {darf('aussondern') && (
                  <td className="handgriff">
                    <button
                      type="button"
                      className="schmal gefahr"
                      disabled={laeuft}
                      onClick={() =>
                        // **Entfernt, nicht stillgelegt**: Eine Mitgliedschaft, die
                        // nie gegolten hat, ist ein Tippfehler. Wer eine Gesellschaft
                        // zu einem Stichtag ausscheiden lassen will, setzt oben ein
                        // Ende und nimmt sie nicht heraus (R316).
                        void tue(() =>
                          api.aussondern('kreis_einheit', m.schluessel, {
                            weg: 'entfernen',
                            abPeriode: null,
                            ziel: null,
                          }),
                        )
                      }
                    >
                      {t('kreis.mitglied.entfernen')}
                    </button>
                  </td>
                  )}
                </tr>
                {kurswahl === m.schluessel && (
                  <tr>
                    <td colSpan={6}>
                      <Eintrittskurswahl
                        kreis={kreis}
                        einheit={einheit}
                        einheitsname={name}
                        von={Number(m.werte.gueltig_von)}
                        bisher={angabe}
                        aufWahl={melde}
                      />
                      <div className="knopfreihe">
                        <button
                          type="button"
                          disabled={laeuft || wahl.felder === null}
                          onClick={() =>
                            void tue(() =>
                              api.stammdatumPflegen('kreis_einheit', m.schluessel, { felder: wahl.felder ?? {} }),
                            )
                          }
                        >
                          {t('kreis.eintrittskurs.speichern')}
                        </button>
                        <button type="button" disabled={laeuft} onClick={() => setKurswahl(null)}>
                          {t('kreis.mitglied.abbrechen')}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/*
        **Aufgenommen wird hier und nicht im Blatt darunter** (R701): Eine
        Mitgliedschaft entsteht an der Stelle, an der sie stehen soll — der
        Kreis steht schon fest, und nach ihm zu fragen wäre eine Frage, deren
        Antwort auf dem Bildschirm steht.

        **Aber erst auf Verlangen** (R315): Die Liste wird gelesen, nicht
        gepflegt — ein Formular, das immer offensteht, kostet drei Zeilen
        Bildschirm für einen Handgriff, den man selten tut.
      */}
      {darf('stammdatumAnlegen') && !anlegen && (
        <div className="knopfreihe">
          <button type="button" disabled={laeuft} onClick={() => setAnlegen(true)}>
            {t('kreis.mitglied.hinzufuegen')}
          </button>
        </div>
      )}

      {darf('stammdatumAnlegen') && anlegen && (
      <div className="stammdatenfelder mitgliedanlage">
        <label>
          <span className="name">{t('kreis.mitglied.gesellschaft')}</span>
          <Auswahlfeld
            wert={entwurf.einheit}
            eintraege={waehlbar}
            leer={t('kreis.mitglied.waehlen')}
            beschriftung={t('kreis.mitglied.gesellschaft')}
            aufWahl={(einheit) => setEntwurf((e) => ({ ...e, einheit }))}
          />
        </label>

        <Periodenfeld
          klasse=""
          wert={entwurf.von}
          beschriftung={t('kreis.mitglied.ab')}
          erlaeuterung={t('kreis.mitglied.ab.hinweis')}
          aufWert={(von) => setEntwurf((e) => ({ ...e, von }))}
        />

        {/* Bilanziert sie in fremder Währung, wird der Eintrittskurs gleich mit gewählt (R1403). */}
        {entwurf.einheit !== '' && entwurf.von !== null && (
          <Eintrittskurswahl
            kreis={kreis}
            einheit={entwurf.einheit}
            einheitsname={gesellschaftsname(entwurf.einheit)}
            von={entwurf.von}
            bisher={null}
            aufWahl={melde}
          />
        )}

        <div className="aktionsreihe">
          <button
            type="button"
            className="haupt"
            disabled={laeuft || entwurf.einheit === '' || entwurf.von === null || (wahl.fremd && wahl.felder === null)}
            onClick={() =>
              void tue(() =>
                // **Ohne Code**: Die Mitgliedschaft hat keinen eigenen -- sie ist
                // die Aussage, daß diese Gesellschaft ab dieser Periode zu
                // diesem Kreis gehört, und die steht in ihren Feldern (R676).
                api.stammdatumAnlegen('kreis_einheit', '', {
                  felder: {
                    kreis,
                    einheit: entwurf.einheit,
                    gueltig_von: String(entwurf.von),
                    ...(wahl.fremd ? (wahl.felder ?? {}) : {}),
                  },
                }),
              )
            }
          >
            {t('kreis.mitglied.aufnehmen')}
          </button>
          <button
            type="button"
            disabled={laeuft}
            onClick={() => {
              setAnlegen(false)
              setEntwurf({ einheit: '', von: null })
              setMeldung(null)
            }}
          >
            {t('kreis.mitglied.abbrechen')}
          </button>
        </div>
      </div>
      )}

      {meldung && <p className="meldung">{meldung}</p>}
    </Blattabschnitt>
  )
}
